import React from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  TextField,
  Chip,
  alpha,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import {
  formatAmountEntry,
  formatDate,
  formatMoney,
} from "../utils/calculator";

const editableMoney = (value) =>
  typeof value === "number" ? formatMoney(value) : formatAmountEntry(value);

const CashflowScheduleReview = ({
  schedule,
  onUpdatePayment,
  onConfirm,
  onBack,
}) => {
  if (!schedule?.length) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.01),
        mt: 2,
      }}
    >
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box>
          <Typography
            variant='h6'
            fontWeight={600}
            color='primary.main'
            gutterBottom
          >
            Review Cashflow Schedule
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Edit payment amounts where required. Dates and periods are fixed.
          </Typography>
        </Box>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Chip
            label={`${schedule.length} periods`}
            size='small'
            sx={{ fontWeight: 600 }}
          />
          {onBack && (
            <Button
              onClick={onBack}
              variant='outlined'
              color='secondary'
              size='small'
              startIcon={<ArrowBackIcon />}
            >
              Back
            </Button>
          )}
        </Stack>
      </Box>

      <TableContainer sx={{ maxHeight: 420 }}>
        <Table stickyHeader size='small'>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Period</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Due date</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
              <TableCell align='right' sx={{ fontWeight: 700 }}>
                Cashflow ($)
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {schedule.map((row, index) => (
              <TableRow key={row.period} hover>
                <TableCell>
                  <Chip
                    label={`T-${row.period}`}
                    size='small'
                    color='primary'
                    variant='outlined'
                    sx={{ fontWeight: 700, fontSize: 11 }}
                  />
                </TableCell>
                <TableCell>{formatDate(row.dueDate)}</TableCell>
                <TableCell>{row.description}</TableCell>
                <TableCell align='right'>
                  {row.editable ? (
                    <Box
                      display='inline-flex'
                      alignItems='center'
                      gap={0.5}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        px: 1,
                        bgcolor: "background.paper",
                      }}
                    >
                      <Typography
                        variant='body2'
                        color='error.main'
                        fontWeight={700}
                      >
                        −
                      </Typography>
                      <TextField
                        variant='standard'
                        value={editableMoney(row.amount)}
                        onChange={(e) => onUpdatePayment(index, e.target.value)}
                        inputProps={{
                          inputMode: "decimal",
                          "aria-label": `Payment for T-${row.period}`,
                          style: {
                            textAlign: "right",
                            fontWeight: 600,
                            width: 100,
                          },
                        }}
                        InputProps={{ disableUnderline: true }}
                      />
                      <EditIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                    </Box>
                  ) : (
                    <Typography
                      component='strong'
                      color='success.main'
                      fontWeight={700}
                    >
                      + {formatMoney(Number(row.amount))}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          p: 2.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <CheckCircleOutlineIcon color='primary' />
          <Box>
            <Typography variant='body2' fontWeight={700}>
              Ready to calculate?
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Confirming locks in this version of your schedule.
            </Typography>
          </Box>
        </Stack>
        <Button variant='contained' color='primary' onClick={onConfirm}>
          Confirm & Calculate
        </Button>
      </Box>
    </Paper>
  );
};

export default CashflowScheduleReview;
